import type { ComponentType } from "react";
import { ChevronDown, ChevronUp, Pencil, Reply as ReplyIcon, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export function MessageActions({
  onReply,
  onEdit,
  onDelete,
  replies,
}: {
  onReply?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  replies?: { count: number; open: boolean; onToggle: () => void };
}) {
  const hasReplies = (replies?.count ?? 0) > 0;
  const hasIconActions = !!onReply || !!onEdit || !!onDelete;
  if (!hasIconActions && !hasReplies) return null;

  return (
    <div className="mt-1 space-y-2">
      {hasIconActions && (
        <TooltipProvider delayDuration={200}>
          <div className="-ml-2 flex items-center">
            {onReply && <IconAction label="Reply" icon={ReplyIcon} onClick={onReply} />}
            {onEdit && <IconAction label="Edit" icon={Pencil} onClick={onEdit} />}
            {onDelete && (
              <>
                {(onReply || onEdit) && (
                  <span aria-hidden className="mx-1 h-4 w-px bg-slate-200" />
                )}
                <IconAction label="Delete" icon={Trash2} onClick={onDelete} destructive />
              </>
            )}
          </div>
        </TooltipProvider>
      )}

      {replies && hasReplies && (
        <button
          type="button"
          onClick={replies.onToggle}
          className="flex items-center gap-1.5 text-xs font-medium text-brand-600 hover:underline"
        >
          {replies.open ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )}
          {replies.open
            ? "Hide replies"
            : `View all ${replies.count} ${replies.count === 1 ? "reply" : "replies"}`}
        </button>
      )}
    </div>
  );
}

function IconAction({
  label,
  icon: Icon,
  onClick,
  destructive = false,
}: {
  label: string;
  icon: ComponentType<{ className?: string }>;
  onClick: () => void;
  destructive?: boolean;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={label}
          onClick={onClick}
          className={cn(
            "h-8 w-8 text-slate-500",
            destructive ? "hover:text-destructive" : "hover:text-slate-900",
          )}
        >
          <Icon />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
